import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';

import { breadcrumbTitleAtom } from 'utils/account/atoms';
import { setTitle } from 'utils/document';

type RouteTitleParams = Record<string, string | undefined>;

type Translate = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

type RouteTitleConfig = {
  exact?: boolean;
  path: string | string[];
  getTitle: (params: RouteTitleParams) => string | undefined;
};

const getDetailTitle =
  (label: string, paramName = 'id') =>
  (params: RouteTitleParams) => {
    const identifier = params[paramName]?.split('-')?.[0]?.toUpperCase();

    return identifier ? `${label} #${identifier}` : label;
  };

/**
 * Browser-tab titles for primary App routes.
 *
 * Keep these paths aligned with the top-level `<Route>` entries in `horizon/App.jsx`
 * (fullRoutes / woCollaboratorRoutes). App.jsx still owns rendering and access control;
 * this table only maps path → document title. The unit test
 * `covers primary App.jsx route paths` guards against silent drift.
 */
export const createRouteTitleConfigs = (t: Translate): RouteTitleConfig[] => [
  { exact: true, path: '/', getTitle: () => t('navigation.dashboard', 'Dashboard') },
  { path: '/analytics', getTitle: () => t('navigation.analytics', 'Analytics') },
  { path: '/alerts', getTitle: () => t('navigation.alerts', 'Alerts') },
  {
    path: ['/transmission-lines', '/transmission-line-uploads'],
    getTitle: () =>
      t('transmissionLines.transmissionLineInspections', 'Transmission Line Inspections'),
  },
  { path: '/settings', getTitle: () => t('navigation.settings', 'Settings') },
  {
    path: '/device-firmware-management',
    getTitle: () => t('navigation.deviceFirmware', 'Device Firmware Management'),
  },
  {
    path: '/executive-dashboard',
    getTitle: () => t('executiveDashboard.title', 'Executive Dashboard'),
  },
  { path: '/fleet', getTitle: () => t('navigation.fleet', 'Fleet') },
  { path: '/uploads', getTitle: () => t('navigation.uploads', 'Uploads') },
  { path: '/damages', getTitle: () => t('navigation.damages', 'Damages') },
  { path: '/damages2/:id', getTitle: getDetailTitle(t('navigation.damages2', 'Damages 2.0')) },
  { exact: true, path: '/damages2', getTitle: () => t('navigation.damages2', 'Damages 2.0') },
  {
    exact: true,
    path: '/inspections/schedule',
    getTitle: () => t('schedule.skySpecsOnlyPageTitle', 'SkySpecs Inspection Schedule'),
  },
  {
    path: '/inspections/:id',
    getTitle: getDetailTitle(t('navigation.inspections', 'Inspections')),
  },
  { exact: true, path: '/inspections', getTitle: () => t('navigation.inspections', 'Inspections') },
  { path: ['/reports', '/report-deliveries'], getTitle: () => t('navigation.reports', 'Reports') },
  { path: '/assets/:id', getTitle: getDetailTitle(t('navigation.assets', 'Assets')) },
  { path: '/assets', getTitle: () => t('navigation.assets', 'Assets') },
  {
    path: '/work-order-create',
    getTitle: () => t('workOrder.createWorkOrder', 'Create Work Order'),
  },
  { path: '/work-orders/:id', getTitle: getDetailTitle(t('navigation.workOrders', 'Work Orders')) },
  { path: '/work-orders', getTitle: () => t('navigation.workOrders', 'Work Orders') },
  { path: '/campaigns/:id', getTitle: getDetailTitle(t('inspection.campaign', 'Campaigns')) },
  { path: '/campaigns', getTitle: () => t('inspection.campaign', 'Campaigns') },
  { path: '/unplanned-tasks', getTitle: () => t('workOrder.unplannedTasks', 'Unplanned Tasks') },
  { path: '/tasks/:id', getTitle: getDetailTitle(t('workOrder.task', 'Tasks')) },
  { path: '/tasks', getTitle: () => t('workOrder.task', 'Tasks') },
  {
    path: '/recurring-task/:id',
    getTitle: getDetailTitle(t('task.recurringTask', 'Recurring Task')),
  },
  {
    exact: true,
    path: '/schedule',
    getTitle: () => t('schedule.pageTitle', 'Schedule'),
  },
  {
    path: ['/pictures', '/picture-review'],
    getTitle: () => t('navigation.pictures', 'Pictures'),
  },
  { exact: true, path: '/reset', getTitle: () => undefined },
  {
    path: '/api/:apiName',
    getTitle: ({ apiName }) =>
      apiName
        ? t('navigation.apiWithName', 'API {{apiName}}', { apiName })
        : t('navigation.api', 'API'),
  },
  { path: '/o/:orgId', getTitle: () => undefined },
];

/** Flattened path patterns from {@link createRouteTitleConfigs} (for drift tests). */
export const getRouteTitlePaths = (configs: RouteTitleConfig[]): string[] =>
  configs.flatMap((config) => (Array.isArray(config.path) ? config.path : [config.path]));

export function getRouteTitle(pathname: string, routeTitleConfigs: RouteTitleConfig[]) {
  for (const config of routeTitleConfigs) {
    const paths = Array.isArray(config.path) ? config.path : [config.path];

    for (const path of paths) {
      const match = matchPath<RouteTitleParams>(pathname, {
        exact: config.exact ?? false,
        path,
      });

      if (match) {
        return config.getTitle(match.params ?? {});
      }
    }
  }

  return undefined;
}

export const RouteTitleManager = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const breadcrumbTitle = useAtomValue(breadcrumbTitleAtom);
  const routeTitleConfigs = useMemo(() => createRouteTitleConfigs(t), [t]);
  useEffect(() => {
    setTitle(breadcrumbTitle ?? getRouteTitle(location.pathname, routeTitleConfigs));
  }, [breadcrumbTitle, location.pathname, routeTitleConfigs]);

  return null;
};
